import * as React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
    createColumnHelper,
    createSortedRowModel,
    rowSortingFeature,
    sortFn_alphanumeric,
    tableFeatures,
    useTable,
} from '@tanstack/react-table';
import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import { getPublishedReviewsInfiniteQuery } from '../../api/publishedReviewsApi';
import type { ClimateVariable, PublishedReviewListModel } from '../../types/publishedReview.types';
import { formatDate } from '../../utils/formatDate';
import { APP_HEADER_OFFSET } from '../../components/layouts/appHeader';
import { useWindowListVirtualizer } from '../../hooks/useWindowListVirtualizer';
import { useFetchMoreOnScroll } from '../../hooks/useFetchMoreOnScroll';
import { useFitColumnSizing, type FitColumn } from '../../hooks/useFitColumnSizing';
import { ResizableHeaderCell } from '../../components/grid/ResizableHeaderCell';
import { gridRowBackground, gridSkeletonBackground } from '../../components/grid/gridBackgrounds';
import { HeaderLabel, SortableHeaderLabel } from '../../components/grid/SortableHeaderCell';

const PAGE_SIZE = 25;
const ROW_HEIGHT_DESKTOP = 56;
const ROW_HEIGHT_MOBILE = 88;
const ROW_HORIZONTAL_PADDING = 32; // px: 2 on each side of header and body rows

// Initial column widths, scaled proportionally to fill the grid's width.
const COLUMN_SIZES: FitColumn[] = [
    { id: 'title', size: 470 },
    { id: 'disease', size: 240 },
    { id: 'climateVariable', size: 140 },
    { id: 'parameterCount', size: 120 },
    { id: 'lastModified', size: 140 },
];

const DEFAULT_SORTING: SortingState = [{ id: 'lastModified', desc: true }];

const sortingToOrderBy = (sort: SortingState[number] | undefined) =>
    sort ? `${sort.id} ${sort.desc ? 'desc' : 'asc'}` : undefined;

const climateVariableMeta: Record<ClimateVariable, { icon: React.ElementType; color: string }> = {
    Temperature: { icon: ThermostatIcon, color: '#e60f00' },
    Rainfall:    { icon: UmbrellaIcon,   color: '#004c79' },
    Humidity:    { icon: WaterDropIcon,  color: '#01a3a8' },
};

export const ClimateVariableLabel = ({ variable }: { variable: ClimateVariable }) => {
    const { icon: Icon, color } = climateVariableMeta[variable];
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Icon sx={{ fontSize: 18, color }} />
            <Typography noWrap sx={{ color: 'text.secondary' }}>{variable}</Typography>
        </Box>
    );
};

// Static table inputs — kept outside the component so feature registration
// and the column helper's type parameters stay stable across renders.
const features = tableFeatures({
    rowSortingFeature,
    sortedRowModel: createSortedRowModel(),
    sortFns: { alphanumeric: sortFn_alphanumeric },
});

const columnHelper = createColumnHelper<typeof features, PublishedReviewListModel>();

const columns = columnHelper.columns([
    columnHelper.accessor('title', {
        header: 'Review',
        cell: ({ getValue }) => <Typography noWrap sx={{ fontWeight: 600 }}>{getValue()}</Typography>,
    }),
    columnHelper.accessor('disease', {
        header: 'Disease',
        cell: ({ getValue }) => <Typography noWrap sx={{ color: 'text.secondary' }}>{getValue()}</Typography>,
    }),
    columnHelper.accessor('climateVariable', {
        header: 'Variable',
        cell: ({ getValue }) => <ClimateVariableLabel variable={getValue()} />,
    }),
    columnHelper.accessor('parameterCount', {
        header: 'Parameters',
        cell: ({ getValue }) => <Typography noWrap sx={{ width: '100%', textAlign: 'right' }}>{getValue()}</Typography>,
    }),
    columnHelper.accessor('lastModified', {
        header: 'Last updated',
        cell: ({ getValue }) => <Typography noWrap sx={{ color: 'text.secondary' }}>{formatDate(getValue())}</Typography>,
    }),
]);

export const PublishedReviewsList = ({ storageKey }: { storageKey: string }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const rowHeight = isMobile ? ROW_HEIGHT_MOBILE : ROW_HEIGHT_DESKTOP;

    const [sorting, setSorting] = React.useState<SortingState>(DEFAULT_SORTING);
    const orderBy = sortingToOrderBy(sorting[0]);

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = useInfiniteQuery(
        getPublishedReviewsInfiniteQuery({ top: PAGE_SIZE, orderBy })
    );

    const reviews = React.useMemo(() => data?.pages.flatMap(page => page.value) ?? [], [data]);
    const totalCount = data?.pages[0]?.['@odata.count'] ?? 0;

    const headingRef = React.useRef<HTMLDivElement>(null);

    const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
        setSorting(updater);
        // Only scroll back when the user has scrolled the heading under the
        // sticky nav — scrolling to the page top would jump back over the hero.
        const heading = headingRef.current;
        if (heading && heading.getBoundingClientRect().top < parseFloat(getComputedStyle(heading).scrollMarginTop)) {
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            heading.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
    };

    const table = useTable(
        {
            features,
            columns,
            data: reviews,
            state: { sorting },
            onSortingChange: handleSortingChange,
            manualSorting: true,
            enableSortingRemoval: false,
            getRowId: (review) => review.id,
        },
        (state) => state
    );

    const rows = table.getRowModel().rows;

    const { listRef, virtualizer, listHeight } = useWindowListVirtualizer({ count: rows.length, rowHeight });

    const virtualItems = virtualizer.getVirtualItems();

    const { containerRef, getColumnWidth, getResizeHandler, resetSizes } = useFitColumnSizing({
        columns: COLUMN_SIZES,
        horizontalPadding: ROW_HORIZONTAL_PADDING,
        storageKey,
    });

    const { checkWindow } = useFetchMoreOnScroll({ hasNextPage, isFetchingNextPage, fetchNextPage });

    React.useEffect(() => {
        // Also check on mount / after data changes, in case the loaded
        // content doesn't yet fill the viewport (nothing to scroll, so no
        // scroll event would ever fire to trigger the next fetch).
        checkWindow();
        window.addEventListener('scroll', checkWindow);
        return () => window.removeEventListener('scroll', checkWindow);
    }, [checkWindow]);

    const renderBody = () => {
        if (isPending) {
            return (
                <Stack alignItems="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                </Stack>
            );
        }

        if (rows.length === 0) {
            return (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                    No reviews published yet.
                </Typography>
            );
        }

        return (
            <Box ref={containerRef}>
                {!isMobile && (
                    <Box
                        sx={{
                            position: 'sticky',
                            top: APP_HEADER_OFFSET,
                            zIndex: (theme) => theme.zIndex.appBar - 1,
                            bgcolor: 'background.default',
                        }}
                    >
                        {table.getHeaderGroups().map(headerGroup => (
                            <Box key={headerGroup.id} sx={{ display: 'flex', alignItems: 'center', width: '100%', px: 2, py: 3, borderBottom: 1, borderColor: 'divider' }}>
                                {headerGroup.headers.map(header => {
                                    const label = header.column.columnDef.header as string;
                                    const content = header.column.getCanSort() ? (
                                        <SortableHeaderLabel
                                            label={label}
                                            isSorted={header.column.getIsSorted()}
                                            onClick={header.column.getToggleSortingHandler()}
                                        />
                                    ) : (
                                        <HeaderLabel label={label} />
                                    );

                                    return (
                                        <ResizableHeaderCell
                                            key={header.id}
                                            width={getColumnWidth(header.column.id)}
                                            onResizeStart={getResizeHandler(header.column.id)}
                                            onResetSize={resetSizes}
                                        >
                                            {content}
                                        </ResizableHeaderCell>
                                    );
                                })}
                            </Box>
                        ))}
                    </Box>
                )}

                <Box
                    ref={listRef}
                    sx={(theme) => ({
                        position: 'relative',
                        width: '100%',
                        height: listHeight,
                        ...gridSkeletonBackground(theme, rowHeight, isMobile),
                    })}
                >
                    {virtualItems.map(virtualRow => {
                        const row = rows[virtualRow.index];
                        if (!row) return null;
                        const review = row.original;

                        return (
                            <Box
                                key={virtualRow.key}
                                onClick={() => navigate({ to: '/published/$reviewId', params: { reviewId: review.id } })}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: virtualRow.size,
                                    transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    cursor: 'pointer',
                                    ...gridRowBackground(theme, virtualRow.index),
                                }}
                            >
                                {isMobile ? (
                                    <ListItemText
                                        primary={review.title}
                                        secondary={`${review.disease} · ${review.climateVariable} · ${formatDate(review.lastModified)}`}
                                        slotProps={{
                                            primary: { noWrap: true, fontWeight: 600 },
                                            secondary: { noWrap: true }
                                        }}
                                    />
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        {row.getAllCells().map(cell => (
                                            <Box
                                                key={cell.id}
                                                style={{ width: getColumnWidth(cell.column.id) }}
                                                sx={{ flexShrink: 0, pr: 3, display: 'flex', alignItems: 'center', minWidth: 0 }}
                                            >
                                                <table.FlexRender cell={cell} />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
                {isFetchingNextPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Stack spacing={2}>
            <Box ref={headingRef} sx={{ scrollMarginTop: APP_HEADER_OFFSET }}>
                <Typography variant="h5" fontWeight={600}>
                    Recent reviews
                </Typography>
                {!isPending && (
                    <Typography variant="body2" color="text.secondary">
                        Showing {reviews.length} of {totalCount}
                    </Typography>
                )}
            </Box>
            {renderBody()}
        </Stack>
    );
};
